/**
 * Session-expiry policy for [NF-008] / [AC-062].
 *
 * Pure functions - no React, no timers, no fetch - so the rules that decide
 * when a person is warned and when the idle limit is reset can be tested
 * directly against an explicit clock.
 *
 * A session has TWO edges, and the warning names whichever comes first:
 *
 *   - the absolute end (`SESSION_MAX`, 12 h): fixed at login, moved by
 *     nothing. The module reports it as `session_ends_at`. Nothing extends it;
 *     the warning only says when it comes. The system account's session ends
 *     with its elevation window ([F-031]) - the same field.
 *   - the idle edge (`SESSION_IDLE`, 4 h): every request the person causes
 *     resets it on the server. The SPA knows when it last made such a request
 *     (lib/session-activity.ts), so the edge is that moment plus the window.
 *
 * Both are announced `SESSION_WARNING_LEAD` (5 min) ahead.
 */

export type SessionEdgeKind = "absolute" | "idle";

export interface SessionClock {
  /** The absolute end, epoch seconds; null when there is no session. */
  endsAt: number | null;
  /** The idle edge, epoch seconds; null without an idle limit or activity. */
  idleEdge: number | null;
  /** SESSION_WARNING_LEAD in seconds. */
  lead: number;
}

export interface SessionWarning {
  kind: SessionEdgeKind;
  /** Seconds until that edge. */
  secondsLeft: number;
  /** The edge itself, epoch seconds. */
  at: number;
}

/** The lead the SRS fixes when the server has not said otherwise ([NF-019]). */
export const DEFAULT_WARNING_LEAD_SECONDS = 5 * 60;

/**
 * Reset the idle limit when the person is active and this share of it is
 * already spent. A request per keystroke would be absurd; waiting until the
 * warning would show an active person a banner they did nothing to deserve.
 * Half is the cheap middle: at 4 h idle, at most one request per 2 h.
 */
export const KEEPALIVE_AFTER_ELAPSED_SHARE = 0.5;

/** ISO timestamp to epoch seconds; null for absent or unreadable. */
export function epochSeconds(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
}

/** The idle edge: last counted activity plus the window. */
export function idleEdgeOf(lastActivityMs: number | null, idleSeconds: number | null | undefined): number | null {
  if (lastActivityMs === null || idleSeconds === null || idleSeconds === undefined || idleSeconds <= 0) return null;
  return Math.floor(lastActivityMs / 1000) + idleSeconds;
}

/** Whichever edge comes first; null when neither is known. */
export function nextEdge(clock: SessionClock): { kind: SessionEdgeKind; at: number } | null {
  const { endsAt, idleEdge } = clock;
  if (endsAt === null && idleEdge === null) return null;
  if (idleEdge === null) return { kind: "absolute", at: endsAt as number };
  if (endsAt === null) return { kind: "idle", at: idleEdge };
  // On a tie the absolute end wins: nothing the person does moves it.
  return idleEdge < endsAt ? { kind: "idle", at: idleEdge } : { kind: "absolute", at: endsAt };
}

/**
 * The warning due now, or null. Only inside the lead before the first edge,
 * and not once the session is gone - an ended session is the auth context's
 * business (it drops to the login), not a countdown's.
 */
export function sessionWarning(clock: SessionClock, nowSeconds: number): SessionWarning | null {
  const edge = nextEdge(clock);
  if (!edge) return null;
  const secondsLeft = edge.at - nowSeconds;
  if (secondsLeft <= 0 || secondsLeft > clock.lead) return null;
  return { kind: edge.kind, secondsLeft, at: edge.at };
}

/**
 * Whether the person's activity should reset the idle limit now - past half
 * of the window, and only while the session is alive.
 */
export function shouldKeepalive(
  idleEdge: number | null,
  idleSeconds: number | null | undefined,
  nowSeconds: number,
): boolean {
  if (idleEdge === null || idleSeconds === null || idleSeconds === undefined || idleSeconds <= 0) return false;
  const left = idleEdge - nowSeconds;
  if (left <= 0) return false;
  const spent = idleSeconds - left;
  return spent >= idleSeconds * KEEPALIVE_AFTER_ELAPSED_SHARE;
}

/** `m:ss` for the countdown; clamped at zero so it never shows a negative. */
export function formatCountdown(secondsLeft: number): string {
  const clamped = Math.max(0, Math.floor(secondsLeft));
  const minutes = Math.floor(clamped / 60);
  const seconds = clamped % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}
